import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Users, 
  Calendar, 
  CheckCircle, 
  Clock, 
  ChevronRight, 
  Loader2, 
  UserPlus, 
  Search, 
  BookOpen,
  TrendingUp,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { studentService } from '../services/studentService';
import { teacherQueryService } from '../services/teacherQueryService';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { toast } from 'sonner';
import { StatCardSkeleton, FormSkeleton } from '../components/ui/skeletons';

const StatCard = ({ icon: Icon, label, value, bgColor, iconColor, loading }) => (
  <Card className="p-6 hover:shadow-lg transition-all duration-300 border-gray-100 bg-white">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        {loading ? (
          <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
        ) : (
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        )}
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon className={`h-6 w-6 ${iconColor}`} />
      </div>
    </div>
  </Card>
);

const QuickActionCard = ({ icon: Icon, title, description, to, bgColor, buttonText }) => (
  <Card className="p-6 hover:shadow-xl transition-all duration-300 border-gray-100 bg-white">
    <div className="flex items-start space-x-4">
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <Link to={to}>
          <Button className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white">
            {buttonText}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  </Card>
);

const SkeletonCard = () => (
  <Card className="p-6 border-gray-100 bg-white">
    <div className="animate-pulse space-y-3">
      <div className="flex items-center space-x-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-3 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  </Card>
);

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    upcomingDemos: 0,
    completedDemos: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [studentsRes, statsRes, queriesRes] = await Promise.all([
        studentService.getStudents(),
        teacherQueryService.getStatistics(),
        teacherQueryService.getQueries(),
      ]);

      setStats({
        totalStudents: studentsRes.data?.length || 0,
        pendingQueries: statsRes.data?.pendingQueries || statsRes.data?.pending || 0,
        resolvedQueries: statsRes.data?.resolvedQueries || statsRes.data?.resolved || 0,
      });

      const sortedQueries = (queriesRes.data?.queries || queriesRes.data || [])
        .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
        .slice(0, 5);
      setRecentBookings(sortedQueries);

      setLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard load error:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-700';
  };

  const customerName = customer?.name || JSON.parse(localStorage.getItem('customerData') || '{}')?.name || 'Customer';

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Customer Dashboard</h1>
        <p className="text-gray-600">Welcome back, {customerName}!</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          icon={Users}
          label="Total Students"
          value={stats.totalStudents}
          bgColor="bg-blue-100"
          iconColor="text-blue-600"
          loading={loading}
        />
        <StatCard
          icon={Clock}
          label="Pending Queries"
          value={stats.pendingQueries}
          bgColor="bg-yellow-100"
          iconColor="text-yellow-600"
          loading={loading}
        />
        <StatCard
          icon={CheckCircle}
          label="Resolved Queries"
          value={stats.resolvedQueries}
          bgColor="bg-green-100"
          iconColor="text-green-600"
          loading={loading}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <QuickActionCard
            icon={UserPlus}
            title="Add Student"
            description="Register a new student to your account"
            to="/customer/students/add"
            bgColor="bg-gradient-to-br from-blue-600 to-teal-500"
            buttonText="Add Student"
          />
          <QuickActionCard
            icon={Search}
            title="Find Tutors"
            description="Search for qualified tutors in your area"
            to="/search-results"
            bgColor="bg-gradient-to-br from-teal-600 to-blue-500"
            buttonText="Search Now"
          />
          <QuickActionCard
            icon={BookOpen}
            title="Submit Query"
            description="Submit your tutor requirements and we'll find the best match"
            to="/customer/teacher-query"
            bgColor="bg-gradient-to-br from-blue-500 to-teal-600"
            buttonText="Submit Query"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
        </div>

        {loading ? (
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : recentBookings.length > 0 ? (
          <Card className="border-gray-100 bg-white overflow-hidden">
            <div className="divide-y divide-gray-100">
              {recentBookings.map((query) => (
                <div 
                  key={query.id} 
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {query.student?.name?.charAt(0) || 'S'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          Query for {query.student?.name || 'Student'}
                        </p>
                        <p className="text-sm text-gray-500">
                          Budget: ₹{query.budget}/month • {query.hours_per_day || query.hoursPerDay} hrs/day
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(query.status)}`}>
                        {query.status}
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center border-gray-100 bg-white">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Recent Activity</h3>
            <p className="text-gray-600 mb-4">You haven't submitted any queries yet.</p>
            <Link to="/customer/teacher-query">
              <Button className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white">
                Submit Query
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
